/**
 * Thai Language Profile
 *
 * SVO word order, prepositions, Thai script.
 * Isolating language (like Chinese) with no inflection.
 */

import type { LanguageProfile } from './types';

export const thaiProfile: LanguageProfile = {
  code: 'th',
  name: 'Thai',
  nativeName: 'ไทย',
  direction: 'ltr',
  script: 'thai',
  wordOrder: 'SVO',
  markingStrategy: 'preposition',
  usesSpaces: false, // Thai doesn't use spaces between words
  defaultVerbForm: 'base',
  verb: {
    position: 'second',
    subjectDrop: true,
  },
  references: {
    me: 'ฉัน',
    it: 'มัน',
    you: 'คุณ',
    result: 'ผลลัพธ์',
    event: 'เหตุการณ์',
    target: 'เป้าหมาย',
    body: 'บอดี้',
  },
  possessive: {
    marker: 'ของ',
    markerPosition: 'between',
    keywords: {
      // Thai uses ของ (khong) + pronoun, but also full possessive phrases
      // "my" - ของฉัน (khong chan), ของผม (khong phom, male speaker)
      ของฉัน: 'me',
      ของผม: 'me',
      // "your" - ของคุณ (khong khun)
      ของคุณ: 'you',
      // "its/his/her" - ของมัน (khong man)
      ของมัน: 'it',
      ของเขา: 'it', // his
      ของเธอ: 'it', // her
    },
  },
  roleMarkers: {
    patient: { primary: '', position: 'before' }, // Thai is isolating - no case markers
    destination: { primary: 'ใน', alternatives: ['ไปยัง'], position: 'before' },
    source: { primary: 'จาก', position: 'before' },
    style: { primary: 'ด้วย', position: 'before' },
    // No `event` role marker: a command's event argument (`trigger foo` /
    // `send foo`) is an UNMARKED object in Thai, like every other SVO profile
    // (es/zh/id/…). `เมื่อ` is the temporal "when/on" marker and belongs only on
    // the event-HANDLER head — which uses `eventHandler.eventMarker` (below),
    // not this role marker. Carrying it here generated `ทริกเกอร์ เมื่อ {event}`,
    // which never matched the transformer's `ทริกเกอร์ {event}` → trigger/send
    // dropped (th behavior-draggable/removable/resizable/sortable lossy).
  },
  keywords: {
    // Class/Attribute operations
    toggle: { primary: 'สลับ', alternatives: [], normalized: 'toggle' },
    add: { primary: 'เพิ่ม', alternatives: [], normalized: 'add' },
    remove: { primary: 'ลบ', alternatives: ['ลบออก'], normalized: 'remove' },
    // Content operations
    put: { primary: 'ใส่', alternatives: ['วาง'], normalized: 'put' },
    append: { primary: 'เพิ่มท้าย', alternatives: [], normalized: 'append' },
    prepend: { primary: 'เพิ่มหน้า', alternatives: [], normalized: 'prepend' },
    take: { primary: 'รับ', alternatives: [], normalized: 'take' },
    make: { primary: 'สร้าง', alternatives: [], normalized: 'make' },
    clone: { primary: 'โคลน', alternatives: ['สำเนา'], normalized: 'clone' },
    swap: { primary: 'สลับที่', alternatives: [], normalized: 'swap' },
    morph: { primary: 'แปลงร่าง', alternatives: [], normalized: 'morph' },
    // Variable operations
    set: { primary: 'ตั้ง', alternatives: ['กำหนด'], normalized: 'set' },
    get: { primary: 'รับค่า', alternatives: [], normalized: 'get' },
    increment: { primary: 'เพิ่มค่า', alternatives: [], normalized: 'increment' },
    decrement: { primary: 'ลดค่า', alternatives: [], normalized: 'decrement' },
    log: { primary: 'บันทึก', alternatives: [], normalized: 'log' },
    // Visibility
    show: { primary: 'แสดง', alternatives: [], normalized: 'show' },
    hide: { primary: 'ซ่อน', alternatives: [], normalized: 'hide' },
    transition: { primary: 'เปลี่ยนผ่าน', alternatives: [], normalized: 'transition' },
    // Events
    on: { primary: 'เมื่อ', alternatives: ['ตอน'], normalized: 'on' },
    trigger: { primary: 'ทริกเกอร์', alternatives: [], normalized: 'trigger' },
    send: { primary: 'ส่ง', alternatives: [], normalized: 'send' },
    // DOM focus
    focus: { primary: 'โฟกัส', alternatives: [], normalized: 'focus' },
    blur: { primary: 'เบลอ', alternatives: [], normalized: 'blur' },
    // Phase 1 (v0.9.90): DOM / form state / debug
    empty: { primary: 'ล้าง', alternatives: ['ทำให้ว่าง', 'ว่าง'], normalized: 'empty' },
    open: { primary: 'เปิด', normalized: 'open' },
    close: { primary: 'ปิด', normalized: 'close' },
    select: { primary: 'ทำเครื่องหมาย', normalized: 'select' },
    clear: { primary: 'เคลียร์', normalized: 'clear' },
    reset: { primary: 'รีเซ็ต', normalized: 'reset' },
    breakpoint: { primary: 'จุดพัก', normalized: 'breakpoint' },
    // Common event names (for event handler patterns)
    click: { primary: 'คลิก', normalized: 'click' },
    hover: { primary: 'โฮเวอร์', alternatives: ['วางเมาส์'], normalized: 'hover' },
    submit: { primary: 'ยื่น', alternatives: ['ส่งข้อมูล'], normalized: 'submit' },
    input: { primary: 'ป้อน', alternatives: ['กรอก'], normalized: 'input' },
    change: { primary: 'เปลี่ยน', alternatives: ['เปลี่ยนแปลง'], normalized: 'change' },
    // Navigation
    go: { primary: 'ไป', alternatives: ['ไปที่'], normalized: 'go' },
    scroll: { primary: 'เลื่อน', normalized: 'scroll' },
    push: { primary: 'ดัน', alternatives: ['ผลัก'], normalized: 'push' },
    replace: { primary: 'แทนที่', alternatives: ['เปลี่ยน-url'], normalized: 'replace' },
    process: { primary: 'ประมวลผล', alternatives: ['จัดการ'], normalized: 'process' },
    // Async
    wait: { primary: 'รอ', alternatives: [], normalized: 'wait' },
    fetch: { primary: 'ดึงข้อมูล', alternatives: [], normalized: 'fetch' },
    settle: { primary: 'คงที่', alternatives: [], normalized: 'settle' },
    // Control flow
    if: { primary: 'ถ้า', alternatives: ['หาก'], normalized: 'if' },
    unless: { primary: 'unless', normalized: 'unless' },
    when: { primary: 'ขณะที่', normalized: 'when' },
    where: { primary: 'ที่ไหน', normalized: 'where' },
    else: { primary: 'ไม่งั้น', alternatives: ['ไม่เช่นนั้น'], normalized: 'else' },
    repeat: { primary: 'ทำซ้ำ', alternatives: [], normalized: 'repeat' },
    for: { primary: 'สำหรับ', alternatives: [], normalized: 'for' },
    while: { primary: 'ในขณะที่', alternatives: [], normalized: 'while' },
    // `repeat forever` loop keyword — corpus word recognized so loopType types
    // as `:literal` like EN (the repeat.loopType R1 residue; see spanish.ts).
    forever: { primary: 'ตลอดไป', normalized: 'forever', alternatives: ['forever'] },
    continue: { primary: 'ต่อไป', alternatives: [], normalized: 'continue' },
    halt: { primary: 'หยุด', alternatives: [], normalized: 'halt' },
    throw: { primary: 'โยน', alternatives: [], normalized: 'throw' },
    call: { primary: 'เรียก', alternatives: [], normalized: 'call' },
    return: { primary: 'คืนค่า', alternatives: ['กลับ'], normalized: 'return' },
    then: { primary: 'แล้ว', alternatives: [], normalized: 'then' },
    and: { primary: 'และ', alternatives: [], normalized: 'and' },
    // Existence operator (`if #modal exists`). Same seam as `matches`: without the
    // keyword the surface stays an identifier and leaks verbatim into the
    // condition's raw expression (if-exists). Neither an ActionType nor a command
    // schema, so no pattern is generated from it.
    exists: { primary: 'มีอยู่', normalized: 'exists' },
    end: { primary: 'จบ', alternatives: [], normalized: 'end' },
    // Advanced
    js: { primary: 'เจเอส', alternatives: ['js'], normalized: 'js' },
    async: { primary: 'อะซิงค์', alternatives: [], normalized: 'async' },
    tell: { primary: 'บอก', alternatives: [], normalized: 'tell' },
    default: { primary: 'ค่าเริ่มต้น', alternatives: [], normalized: 'default' },
    init: { primary: 'เริ่มต้น', alternatives: [], normalized: 'init' },
    behavior: { primary: 'พฤติกรรม', alternatives: [], normalized: 'behavior' },
    install: { primary: 'ติดตั้ง', alternatives: [], normalized: 'install' },
    measure: { primary: 'วัด', alternatives: [], normalized: 'measure' },
    beep: { primary: 'บี๊บ', alternatives: [], normalized: 'beep' },
    break: { primary: 'ขัดจังหวะ', alternatives: [], normalized: 'break' },
    copy: { primary: 'คัดลอก', alternatives: [], normalized: 'copy' },
    exit: { primary: 'ออก', alternatives: [], normalized: 'exit' },
    pick: { primary: 'เลือก', alternatives: [], normalized: 'pick' },
    render: { primary: 'แสดงผล', alternatives: [], normalized: 'render' },
    // Modifiers
    into: { primary: 'ใน', alternatives: [], normalized: 'into' },
    before: { primary: 'ก่อน', alternatives: [], normalized: 'before' },
    after: { primary: 'หลัง', alternatives: [], normalized: 'after' },
    until: { primary: 'จนถึง', alternatives: [], normalized: 'until' },
    event: { primary: 'เหตุการณ์', alternatives: [], normalized: 'event' },
    from: { primary: 'จาก', normalized: 'from' },
    // Phase 8 (htmx v4 localized attributes): attribute-suffix keywords
    // used by the vocab generator — `sse-เชื่อมต่อ`, `hx-ไลฟ์`, etc.
    connect: { primary: 'เชื่อมต่อ', alternatives: ['เชื่อม'], normalized: 'connect' },
    stream: { primary: 'สตรีม', alternatives: ['สตรีมมิ่ง', 'กระแส'], normalized: 'stream' },
    live: { primary: 'ไลฟ์', alternatives: ['สด', 'ถ่ายทอดสด'], normalized: 'live' },
    socket: {
      primary: 'ซ็อกเก็ต',
      alternatives: ['websocket', 'เว็บซ็อกเก็ต', 'socket'],
      normalized: 'socket',
    },
    // Service-worker / realtime keywords (mirrors #262 ja/he wiring; native
    // primary + English alternative, eventsource keeps the canonical English form)
    intercept: {
      primary: 'ดักจับ',
      alternatives: ['intercept', 'สกัดกั้น'],
      normalized: 'intercept',
    },
    worker: { primary: 'เวิร์กเกอร์', alternatives: ['worker'], normalized: 'worker' },
    eventsource: {
      primary: 'eventsource',
      alternatives: ['อีเวนต์ซอร์ส'],
      normalized: 'eventsource',
    },
    // Reactive: bind (generated text keeps the English verb; native primary
    // for future i18n dictionary support)
    bind: { primary: 'ผูก', alternatives: ['bind'], normalized: 'bind' },
  },
  tokenization: {
    boundaryStrategy: 'character', // Character-based like Chinese/Japanese
  },
  eventHandler: {
    keyword: { primary: 'เมื่อ', alternatives: ['ตอน'], normalized: 'on' },
    sourceMarker: { primary: 'จาก', position: 'before' },
    // Event marker: เมื่อ (when), used in SVO pattern
    // Pattern: เมื่อ [event] [verb] [patient] ใน [destination?]
    // Example: เมื่อ คลิก สลับ .active ใน #button
    eventMarker: { primary: 'เมื่อ', alternatives: ['ตอน'], position: 'before' },
    temporalMarkers: ['เมื่อ', 'ตอน'], // temporal conjunctions (when)
  },
};
